import { getCompanyLegacyId, getToken } from ".";
import { HttpException } from "../../models/exceptions";
import {
  Template,
  TemplateConfig,
  TemplateContent,
  TemplateKey,
  TemplateKeyAndCategory,
  TemplateNavigationPath,
  TemplatePatchReturn,
  TemplatePutReturn,
  TemplateRenderReturn,
  TemplatesSummary,
  TemplateType,
  Translation,
  TranslationsToUpdate,
  Version,
} from "../../models/templates";
import store from "../../store";

export async function fetchTemplatesSummary({ signal }: { signal: any }): Promise<Array<TemplatesSummary>> {
  const companyLegacyId = await getCompanyLegacyId();
  const token = await getToken();

  let url = `${(window as any)._env_.REACT_APP_COLIBRY_SERVICES_HOST}/api/2/customers/${companyLegacyId}/templates/summary`;

  const response = await fetch(url, {
    signal: signal,
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while fetching the templates.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  let summary = await response.json();

  summary.forEach((s: TemplatesSummary) => {
    s.navigationPath = {
      namespace: s.key.namespace,
      category: s.category,
      name: s.key.name,
    };
  });

  return summary;
}

export async function fetchTemplate({
  signal,
  key,
  version,
}: {
  signal: any;
  key: TemplateKey | TemplateNavigationPath;
  version: Version;
}): Promise<Template | null> {
  // If we provide a version, we expect only one version to be returned
  const templateVersions = await fetchTemplates({ signal, key, version });
  return templateVersions.length > 0 ? templateVersions[0] : null;
}

export async function fetchTemplates({
  signal,
  key,
  version,
}: {
  signal: any;
  key: TemplateKey | TemplateNavigationPath;
  version: Version | null;
}): Promise<Array<Template>> {
  const companyLegacyId = await getCompanyLegacyId();
  const token = await getToken();

  let url = `${(window as any)._env_.REACT_APP_COLIBRY_SERVICES_HOST}/api/2/customers/${companyLegacyId}/templates/${key.namespace}/${
    key.name
  }/${version}`;

  const response = await fetch(url, {
    signal: signal,
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while fetching the templates.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  let templates = await response.json();

  return templates;
}

export async function putTemplate({
  key,
  category,
  type,
  config = null,
  content = null,
}: {
  key: TemplateKey | TemplateNavigationPath;
  category: string;
  type: TemplateType;
  config?: TemplateConfig | null;
  content?: TemplateContent | null;
}) : Promise<TemplatePutReturn> {
  const companyLegacyId = await getCompanyLegacyId();
  const token = await getToken();

  const url = `${(window as any)._env_.REACT_APP_COLIBRY_SERVICES_HOST}/api/2/customers/${companyLegacyId}/templates/${key.namespace}/${
    key.name
  }`;

  const response = await fetch(url, {
    method: "PUT",
    body: JSON.stringify({
      category,
      type,
      config,
      content,
    }),
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while storing the template.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  return await response.json();
}

export async function patchTemplate({ key, dataToUpdate }: { key: TemplateKey | TemplateNavigationPath; dataToUpdate: any }) : Promise<TemplatePatchReturn> {
  const companyLegacyId = await getCompanyLegacyId();
  const token = await getToken();

  const url = `${(window as any)._env_.REACT_APP_COLIBRY_SERVICES_HOST}/api/2/customers/${companyLegacyId}/templates/${key.namespace}/${
    key.name
  }`;

  const response = await fetch(url, {
    method: "PATCH",
    body: JSON.stringify({
      ...dataToUpdate,
    }),
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while patching the template.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  return await response.json();
}

export async function deleteTemplate({
  key,
  version,
}: {
  key: TemplateKey | TemplateNavigationPath;
  version: Version | null;
}): Promise<Record<string, string>> {
  const companyLegacyId = await getCompanyLegacyId();
  const token = await getToken();

  let url = `${(window as any)._env_.REACT_APP_COLIBRY_SERVICES_HOST}/api/2/customers/${companyLegacyId}/templates/${key.namespace}/${
    key.name
  }/${version}`;

  const response = await fetch(url, {
    method: "DELETE",
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while deleting the template.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  let info = await response.json();

  return info;
}

export async function promoteTemplate({ key, translationKeys }: { key: TemplateKey | TemplateNavigationPath, translationKeys: string[] }): Promise<void> {
  const companyLegacyId = await getCompanyLegacyId();
  const token = await getToken();

  const url = `${(window as any)._env_.REACT_APP_COLIBRY_SERVICES_HOST}/api/2/customers/${companyLegacyId}/templates/promote/${
    key.namespace
  }/${key.name}`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      translation_keys: translationKeys,
    }),
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while promoting the template.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  return;
}

// export async function postCopyToDraft({ templatePath, version }: { templatePath: TemplatePath; version: Version }) {
//   const companyLegacyId = await getCompanyLegacyId();
//   const token = await getToken();

//   const url = `${(window as any)._env_.REACT_APP_COLIBRY_SERVICES_HOST}/api/2/customers/${companyLegacyId}/templates/copyToDraft/${
//     templatePath.namespace
//   }/${templatePath.type}/${templatePath.name}/${version}`;

//   const response = await fetch(url, {
//     method: "POST",
//     headers: {
//       Authorization: "Bearer " + token,
//       "Content-Type": "application/json",
//     },
//   });

//   if (!response.ok) {
//     const error = new HttpException("An error occurred while copying to draft.");
//     error.code = response.status;
//     error.info = await response.json();
//     throw error;
//   }

//   return;
// }

export async function renderTemplate({
  key,
  version,
  showPureHtml = false,
}: {
  signal: AbortSignal;
  key: TemplateKey | TemplateNavigationPath;
  version: Version;
  showPureHtml?: boolean;
}): Promise<TemplateRenderReturn> {
  const companyLegacyId = await getCompanyLegacyId();
  const token = await getToken();

  const url = `${(window as any)._env_.REACT_APP_COLIBRY_SERVICES_HOST}/api/2/customers/${companyLegacyId}/templates/render/${
    key.namespace
  }/${key.name}/${version}`;

  const renderingVariables = store.getState().templates.renderingVariables;
  const language = store.getState().templates.selectedLanguage;

  const response = await fetch(url, {
    method: "POST",
    body: JSON.stringify({
      language: language,
      fields: JSON.parse(renderingVariables),
      showPureHtml,
    }),
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while storing the template.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  return response.json();
}

export async function fetchTranslations({
  signal,
  keys,
}: {
  signal?: AbortSignal;
  keys: string[];
}): Promise<{ [key: string]: Translation[] }> {
  const companyLegacyId = await getCompanyLegacyId();
  const token = await getToken();

  let url = `${(window as any)._env_.REACT_APP_COLIBRY_SERVICES_HOST}/api/2/customers/${companyLegacyId}/templates/translations:batchGet`;

  const response = await fetch(url, {
    signal,
    method: "POST",
    body: JSON.stringify({
      keys: keys,
    }),
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while fetching the templates.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  let translations = await response.json();

  return translations;
}

export async function fetchTemplateUsage({
  signal,
  key,
  category
}: {
  signal?: AbortSignal;
  key: TemplateKey | TemplateNavigationPath;
  category?: string;
}): Promise<TemplateKeyAndCategory[]> {
  const companyLegacyId = await getCompanyLegacyId();
  const token = await getToken();

  let url = `${(window as any)._env_.REACT_APP_COLIBRY_SERVICES_HOST}/api/2/customers/${companyLegacyId}/templates/usage/${key.namespace}/${key.name}`;

  const response = await fetch(url, {
    signal,
    method: "POST",
    body: JSON.stringify({
      category,
    }),
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while fetching the templates.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  let automationsUsingTemplate = await response.json();

  return automationsUsingTemplate;
}


export async function fetchTranslationUsage({
  signal,
  keys,
}: {
  signal?: AbortSignal;
  keys: string[];
}): Promise<{ [key: string]: TemplateKeyAndCategory[] }> {
  const companyLegacyId = await getCompanyLegacyId();
  const token = await getToken();

  let url = `${(window as any)._env_.REACT_APP_COLIBRY_SERVICES_HOST}/api/2/customers/${companyLegacyId}/templates/translationUsage`;

  const response = await fetch(url, {
    signal,
    method: "POST",
    body: JSON.stringify({
      translations: keys,
      category: "automations",
    }),
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while fetching the templates.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  let translationsWithAutomations = await response.json();

  return translationsWithAutomations;
}

export async function updateTranslations({
  translationsToUpdate,
}: {
  translationsToUpdate: TranslationsToUpdate;
}): Promise<undefined> {
  const companyLegacyId = await getCompanyLegacyId();
  const token = await getToken();

  let url = `${(window as any)._env_.REACT_APP_COLIBRY_SERVICES_HOST}/api/2/customers/${companyLegacyId}/templates/translations`;

  const updates = [];
  for (const key in translationsToUpdate) {
    updates.push({
      key: `${key}`,
      value: translationsToUpdate[key],
    });
  }

  const response = await fetch(url, {
    method: "PUT",
    body: JSON.stringify(updates),
    headers: {
      Authorization: "Bearer " + token,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = new HttpException("An error occurred while fetching the templates.");
    error.code = response.status;
    error.info = await response.json();
    throw error;
  }

  return;
}
